import React, { useLayoutEffect, useMemo } from "react";
import { useEffect, useRef, useState } from "react";
import {
  Link,
  NavLink,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Eye,
  Grid,
  List,
  MapPin,
  Share2,
  Briefcase,
  GraduationCap,
} from "lucide-react";
import { getRequest } from "../../helpers/api.helper";
import { UPLOAD_URL } from "../../utils/constants";
import { MatchListEntry, ProfileType } from "../../types";
import { calculateAge, timeAgo } from "../../helpers/function.helper";
import { FaSearch } from "react-icons/fa";
import ProfileListItem from "../../components/MatchesContainer/partials/ProfileListItem";
import GridListItem from "../../components/MatchesContainer/partials/GridListItem";
import { toast } from "react-toastify";
import ShareModal from "../../components/ShareModal";
import ShareBiodataModal from "../../components/ShareBiodataModal";

const WebsiteProfiles = () => {
  const { id, gender, name } = useParams();
  const [profiles, setProfiles] = useState<MatchListEntry[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState();
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isShareBiodataModalOpen, setIsShareBiodataModalOpen] = useState(false);
  const [shareBiodataType, setShareBiodataType] = useState<
    "pdf" | "image" | null
  >(null);
  const [selectedMatch, setSelectedMatch] = useState<MatchListEntry | null>(
    null
  );
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false); // ✅ prevents duplicate API calls

  const navigate = useNavigate();

  const location = useLocation();

  // unique key per website+gender+page url
  const scrollKey = useMemo(
    () => `webscroll:${location.pathname}${location.search}`,
    [location.pathname, location.search]
  );

  // ✅ Fetch profiles with pagination
  const fetchProfiles = async (pageNum = 1) => {
    if (isFetchingRef.current) return; // block overlapping requests
    isFetchingRef.current = true;
    setLoading(true);

    try {
      const response = await getRequest(
        `/bureau/website/${id}/profiles/${gender}?page=${pageNum}&limit=10`
      );
      if (response.success) {
        const newProfiles =
          response.data?.profiles || response.data?.data || [];
        setCount(response.data.count);

        if (newProfiles.length === 0) {
          setHasMore(false);
        } else {
          setProfiles((prev) =>
            pageNum === 1 ? newProfiles : [...prev, ...newProfiles]
          );
          setPage(pageNum);
        }
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching profiles:", error);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  };

  const pageRef = useRef(1);
  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    setProfiles([]);
    setPage(1);
    pageRef.current = 1; // ✅ keep ref in sync
    setHasMore(true);
    fetchProfiles(1); // ✅ fetch first page
  }, [id, gender, scrollKey]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !loading &&
          hasMore &&
          !isFetchingRef.current
        ) {
          fetchProfiles(pageRef.current + 1);
        }
      },
      { rootMargin: "300px" }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading]);

  const listReady = !loading && profiles.length > 0;

  useLayoutEffect(() => {
    if (!listReady) return;

    const raw = sessionStorage.getItem(scrollKey);
    if (!raw) return;

    const y = Number(raw);
    if (!Number.isFinite(y)) return;

    requestAnimationFrame(() => window.scrollTo(0, y));
    const t = setTimeout(() => {
      window.scrollTo(0, y);
      sessionStorage.removeItem(scrollKey); // ✅ add this
    }, 200);

    return () => clearTimeout(t);
  }, [listReady, scrollKey]);

  // ✅ Infinite scroll setup (fixed)
  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          !loading &&
          hasMore &&
          !isFetchingRef.current
        ) {
          fetchProfiles(page + 1);
        }
      },
      { rootMargin: "300px" } // start preloading before end
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading]); // ✅ no “page” dependency (prevents infinite loop)

  useEffect(() => {
    const handleBiodataShare = (event: any) => {
      if (!selectedMatch) return;

      const type = event.detail?.type;
      if (type === "pdf" || type === "image") {
        setShareBiodataType(type);
        setIsShareBiodataModalOpen(true);
      }
    };

    window.addEventListener("shareBiodata", handleBiodataShare);

    return () => {
      window.removeEventListener("shareBiodata", handleBiodataShare);
    };
  }, [selectedMatch]);

  const handleShareProfile = (match: MatchListEntry) => {
    setSelectedMatch(match);
    setIsShareModalOpen(true);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* 🔹 Sticky Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white/90 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            {/* Title */}
            <div className="flex flex-col">
              <h1 className="text-xl font-bold text-pink-600">
                {gender === "male" ? "Male Profiles" : "Female Profiles"}
              </h1>
              <span className="text-xs">
                showing {profiles.length} of {count}
              </span>
            </div>

            {/* Search + View Mode Buttons */}
            <div className="flex items-center gap-3">
              {/* Search Button */}
              <div className="rounded-lg bg-gray-100 p-2">
                <NavLink to={`/${id}/${name}/search`}>
                  <FaSearch
                    size={18}
                    className="text-gray-600 hover:text-pink-600"
                  />
                </NavLink>
              </div>

              {/* View Toggle Buttons */}
              <div className="flex gap-2 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setViewMode("list")}
                  className={`p-2 rounded transition-all ${
                    viewMode === "list"
                      ? "bg-white text-pink-600 shadow-sm"
                      : "text-gray-600 hover:text-pink-600"
                  }`}
                >
                  <List size={20} />
                </button>
                <button
                  onClick={() => setViewMode("grid")}
                  className={`p-2 rounded transition-all ${
                    viewMode === "grid"
                      ? "bg-white text-pink-600 shadow-sm"
                      : "text-gray-600 hover:text-pink-600"
                  }`}
                >
                  <Grid size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 🔸 Main Content — push down so it doesn’t hide under sticky header */}
      <div className="max-w-7xl mx-auto px-4 pt-24 pb-10">
        {viewMode === "list" ? (
          <div className="space-y-4">
            {profiles.map((match: MatchListEntry) => {
              return (
                <ProfileListItem
                  key={match.id}
                  match={match}
                  onViewProfile={() => {
                    sessionStorage.setItem(scrollKey, String(window.scrollY));
                    navigate(`/${id}/${name}/${match.maritalId}`);
                  }}
                  matches={profiles}
                  setProfiles={() => {}}
                  onShareProfile={() => handleShareProfile(match)}
                  isOtherPage={true}
                />
              );
            })}
          </div>
        ) : (
          // 🟣 GRID VIEW
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {profiles.map((match: any) => {
              return (
                <GridListItem
                  key={match.maritalId}
                  match={match}
                  onViewProfile={() => {
                    sessionStorage.setItem(scrollKey, String(window.scrollY));
                    navigate(`/${id}/${name}/${match.maritalId}`);
                  }}
                  onShareProfile={() => handleShareProfile(match)}
                />
              );
            })}
          </div>
        )}

        {/* Infinite Scroll / Loading / End Indicators */}
        {hasMore && !loading && (
          <div ref={loadMoreRef} className="h-6 w-full" />
        )}
        {loading && (
          <div className="text-center py-6 text-gray-500">Loading...</div>
        )}
        {!hasMore && profiles.length > 0 && (
          <div className="text-center py-6 text-gray-400 text-sm">
            You’ve reached the end.
          </div>
        )}
        {isShareModalOpen && selectedMatch && (
          <ShareModal
            profile={selectedMatch}
            setIsShareModalOpen={setIsShareModalOpen}
          />
        )}

        {isShareBiodataModalOpen && shareBiodataType && selectedMatch && (
          <ShareBiodataModal
            profile={selectedMatch}
            setIsShareBiodataModalOpen={setIsShareBiodataModalOpen}
            shareType={shareBiodataType}
          />
        )}
      </div>
    </div>
  );
};

export default WebsiteProfiles;
